import classNames from 'classnames';
import React, { useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { formatCurrencyValue } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';

import ANALYTICS_CATEGORIES from '../../../../analytics/categories';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import { useFormContext } from '../../../../hooks/useForm';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import { AnalyticsEvents, trackEvent } from '../../../../services/analytics';
import { useSigningContext } from '../../../../signingContext/signingContext';
import {
    AmountInput,
    DateSelect,
    FromAccountSelect,
    ReceiverComboBox,
} from '../../../NettbankCreateTransactions/components';
import { MessageInput } from '../../../fields';
import DesktopDropdownPlaceholder from '../../DesktopDropdownPlaceholder';
import { PaymentError } from '../../Payment/form/validate';
import { PaymentAction, PaymentActionType } from '../form/actions';
import { EfakturaState } from '../form/state';
import { amountLimitWarning, isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';

const EFakturaDesktopFields: React.FC<FieldProps> = ({
    showMinAmount,
    locked,
    accounts,
    invalidAccounts,
    accountState,
    emptyFieldWarningLabel,
    bottomLabel,
    waitingForReceiver,
    date,
    useDesktopPlaceholder = true,
    paymentLimits,
    messageAllowed,
}) => {
    const { signingOperationPending } = useSigningContext();
    const { state, errors, handleChange } = useFormContext<EfakturaState, PaymentAction, PaymentError>();
    const { minDate, hasChangedDate, setChangedDate } = date;
    const translated = useTranslation();
    const [searchString, setSearchString] = useState('');
    const { targetFromAccount } = usePaymentAccountContext();
    const showLock = locked || signingOperationPending;

    const [placeholderHeight, setPlaceholderHeight] = useState<number>(0);
    return (
        <>
            <FromAccountSelect
                className={classNames('transaction-form__from-account-selector open', {
                    error: errors.accountId || errors.toAccount,
                })}
                id="efaktura-from-account"
                label={translated.from}
                items={accounts ?? []}
                onSelect={(item) => {
                    handleChange({
                        type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                        payload: item,
                    });
                    trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'select_from_account');
                }}
                placeholder={translated.payFromAccount}
                locked={showLock}
                isLoading={accountState.isLoading}
                selected={state.fromAccount}
                selectedAccount={targetFromAccount}
                invalidItems={invalidAccounts}
                error={
                    errors.accountId
                        ? { className: 'transaction-form__from-account-error', message: errors.accountId }
                        : undefined
                }
                isPaymentModalOpen
                bottomLabel={emptyFieldWarningLabel ?? bottomLabel}
                setPlaceholderHeight={setPlaceholderHeight}
            />
            <ReceiverComboBox
                className={classNames('transaction-form__to-account-selector open', {
                    error: errors.toAccount || errors.accountId,
                })}
                id="efaktura-to-account"
                label={translated.to}
                placeholder={translated.addReceiver}
                searchString={
                    searchString.length === 0 && state.toAccount?.accountName
                        ? state.toAccount?.accountName
                        : searchString
                }
                setSearchString={(value: string) => setSearchString(value)}
                icon={IconTypes.PERSONER_24}
                items={[]}
                onSelect={() => null}
                selected={state.toAccount}
                locked
                isLoading={waitingForReceiver}
                onShowAddReceiver={() => null}
                addReceiverText={translated.addNewReceiver}
                isPaymentModalOpen
                tabIndex={-1}
            />
            <AmountInput
                className={classNames('transaction-form__amount-selector', {
                    error: errors.amount || errors.amountLimit || showMinAmount,
                })}
                value={state.amount}
                label={translated.payAmount}
                error={errors.amount}
                limitError={errors.amountLimit}
                limitWarning={amountLimitWarning(translated, state, paymentLimits, targetFromAccount)}
                onChange={(value) => {
                    handleChange({ type: PaymentActionType.AMOUNT, payload: value });
                    trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'set_amount');
                }}
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                locked={showLock}
                currency={targetFromAccount?.currency}
                infoMessage={
                    showMinAmount
                        ? format(translated.minAmountToPay, formatCurrencyValue(state.minimumAmount!))
                        : undefined
                }
            />
            <MessageInput
                noteValue={messageAllowed ? state.note : undefined}
                className={classNames('transaction-form__message-selector', { error: errors.note })}
                onChange={(note) => {
                    if (messageAllowed) handleChange({ type: PaymentActionType.NOTE, payload: note });
                }}
                kidValue={state.kid?.value}
                locked={state.hasInitialKid}
                disableKid={(state.toAccount?.kidFormats?.length ?? []) === 0 || !state.hasInitialKid}
                selectedReceiver={state.toAccount}
                isKidOnly={state.toAccount?.kidRequired || state.hasInitialKid}
            />
            <DateSelect
                className={classNames('transaction-form__date-selector', { error: errors.paymentDate })}
                date={state.paymentDate ?? getZonedDate()}
                onChange={(date) => {
                    setChangedDate(false);
                    handleChange({ type: PaymentActionType.PAYMENT_DATE, payload: date });
                    trackEvent(AnalyticsEvents.EFAKTURA, ANALYTICS_CATEGORIES.EFAKTURA, 'select_date');
                }}
                error={errors.paymentDate}
                locked={showLock}
                onlyBusinessDays
                minDate={minDate}
                warning={hasChangedDate ? translated.eFaktura.eFakturaChangedDate : undefined}
            />
            {useDesktopPlaceholder && <DesktopDropdownPlaceholder height={placeholderHeight} />}
        </>
    );
};

export default EFakturaDesktopFields;
